import React, { useState, useEffect } from "react";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Addemployee from "./Addemployee";
import { DeleteEmployeeByID, getAllEmployees } from "../../../api/api";
import toast from "react-hot-toast";
import ConfirmDialog from "../../Delete/ConfirmDialog";

const EmployeeTable = () => {
  const [addEmployeeOpen, setEmployeeopen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isView, setIsView] = useState(false);

  // Pagination and Search
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [isLoading, setIsLoading] = useState(false);

  const rowsPerPage = 10;

  const handleAddEmployee = () => {
    setEmployeeopen(true);
    setCurrentEmployee(null);
  };

  const handleBackEmployee = () => {
    setEmployeeopen(false);
    setIsView(false);
    setIsEdit(false);
  };

  const handleView = (employee) => {
    setIsEdit(false);
    setIsView(true);
    setCurrentEmployee(employee);
    setEmployeeopen(true);
  };

  const handleEdit = (employee) => {
    setIsEdit(true);
    setIsView(false);
    setCurrentEmployee(employee);
    setEmployeeopen(true);
  };

  const handleDelete = (employee) => {
    setEmployeeToDelete(employee);
    setConfirmDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (employeeToDelete) {
      DeleteEmployeeByID(employeeToDelete._id)
        .then(() => {
          toast.success(`${employeeToDelete.name} has been deleted.`);
          setEmployees((prevEmployees) =>
            prevEmployees.filter((emp) => emp._id !== employeeToDelete._id)
          );
          setEmployeeToDelete(null);
          fetchEmployees(); // Refresh the list
        })
        .catch((err) => toast.error(err.message));
    }
    setConfirmDialogOpen(false);
  };

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const res = await getAllEmployees(currentPage, rowsPerPage, searchTerm);
      setEmployees(res.data.employees);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      toast.error("Failed to fetch employees");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [currentPage,debouncedSearch, addEmployeeOpen]);

  useEffect(() => {
  const handler = setTimeout(() => {
    setDebouncedSearch(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
  }, 500); // 500ms debounce delay

  return () => {
    clearTimeout(handler); // Clear timeout if value changes before delay
  };
}, [searchTerm]);


  return (
    <div>
      {!addEmployeeOpen ? (
        <div>
          <div className="flex justify-between items-center px-4">
            <h1 className="text-2xl font-bold">Employees</h1>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="border px-2 py-1 rounded"
              />
              <button
                className="rounded-md py-2 px-4 bg-black text-white text-xs uppercase"
                onClick={handleAddEmployee}
              >
                Add Employee
              </button>
            </div>
          </div>

          <div className="w-full overflow-x-auto mt-4 px-4">
            <table className="w-full whitespace-no-wrap">
              <thead>
                <tr className="text-xs poppins-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                  <th className="px-4 py-3">S.No</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Joining Date</th>
                  <th className="px-4 py-3">Documents</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : employees.length > 0 ? (
                  employees.map((employee, index) => (
                    <tr key={employee._id} className="text-gray-700">
                      <td className="px-4 py-3 text-sm">
                        {(currentPage - 1) * rowsPerPage + index + 1}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold">
                        {employee.name}
                      </td>
                      <td className="px-4 py-3 text-sm">{employee.contactNo}</td>
                      <td className="px-4 py-3 text-sm">{employee.role}</td>
                      <td className="px-4 py-3 text-sm">
                        {employee.joiningDate?.slice(0, 10)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        {employee.doc && (
                          <div
                            className="text-blue-500 underline cursor-pointer"
                            onClick={() => window.open(employee.uploaddoc, "_blank")}
                          >
                            {employee.doc}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex space-x-2">
                          <EyeIcon
                            className="h-5 w-5 text-gray-500 hover:text-black cursor-pointer"
                            onClick={() => handleView(employee)}
                          />
                          <PencilIcon
                            className="h-5 w-5 text-gray-500 hover:text-black cursor-pointer"
                            onClick={() => handleEdit(employee)}
                          />
                          <TrashIcon
                            className="h-5 w-5 text-gray-500 hover:text-black cursor-pointer"
                            onClick={() => handleDelete(employee)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      No employees found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center mt-4 space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === i + 1 ? "bg-black text-white" : "bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>

          {/* Delete confirmation */}
          <ConfirmDialog
            isOpen={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={handleDeleteConfirm}
            title="Delete Employee"
            message="Are you sure you want to delete this employee?"
          />
        </div>
      ) : (
        <Addemployee
          onBack={handleBackEmployee}
          isEdit={isEdit}
          isView={isView}
          employee={currentEmployee}
        />
      )}
    </div>
  );
};

export default EmployeeTable;
